import React, { useState } from 'react';
import { SlidersHorizontal, AlertCircle } from 'lucide-react';
import useProductCatalog from '../../hooks/useProductCatalog';
import SearchFiltersPanel from './SearchFiltersPanel';
import SearchFiltersMobileDrawer from './SearchFiltersMobileDrawer';
import ActiveFiltersChips from './ActiveFiltersChips';
import ResultsHeader from './ResultsHeader';
import Pagination from './Pagination';
import ProductCard from '../ProductCard';

/**
 * ProductCatalogView — Composant catalogue Retailer réutilisable (Phase 3.4.2)
 *
 * @param {Object} contextFilters - Filtres implicites de la page
 * @param {string} initialSort - Tri initial
 * @param {number} pageSize - Taille de page
 * @param {boolean} syncURL - Synchroniser filtres avec URL
 * @param {string} apiEndpoint - '/products' ou '/listings/{type}'
 * @param {React.ReactNode} header - Contenu au-dessus des résultats
 * @param {string} emptyMessage - Message si aucun résultat
 * @param {Function} renderProductExtra - Rendu additionnel sous chaque card
 */
export default function ProductCatalogView({
  contextFilters = {},
  initialSort = '',
  pageSize = 24,
  syncURL = false,
  apiEndpoint = '/products',
  header,
  emptyMessage = 'Aucun produit ne correspond à vos critères.',
  renderProductExtra,
}) {
  const catalog = useProductCatalog({ contextFilters, initialSort, pageSize, syncURL, apiEndpoint });
  const [mobileOpen, setMobileOpen] = useState(false);

  const hasUserFilters = Object.keys(catalog.filters).length > 0;

  return (
    <div className="flex gap-6" data-testid="product-catalog-view">
      {/* SIDEBAR — Desktop only */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-4 max-h-[calc(100vh-6rem)] overflow-y-auto pr-2 pb-8">
          <SearchFiltersPanel
            filters={catalog.filters}
            setFilter={catalog.setFilter}
            clearFilter={catalog.clearFilter}
            clearAllFilters={catalog.clearAllFilters}
            contextFilters={contextFilters}
            apiEndpoint={apiEndpoint}
          />
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="flex-1 min-w-0">
        {header}

        {/* Mobile filter button */}
        <div className="lg:hidden mb-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="open-mobile-filters"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filtres
            {hasUserFilters && (
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-800 text-white text-[10px] font-bold">
                {Object.keys(catalog.filters).length}
              </span>
            )}
          </button>
        </div>

        {/* Active filters chips */}
        {hasUserFilters && (
          <div className="mb-3">
            <ActiveFiltersChips
              filters={catalog.filters}
              onClear={catalog.clearFilter}
              onClearAll={catalog.clearAllFilters}
            />
          </div>
        )}

        {/* Results header */}
        <ResultsHeader
          total={catalog.total}
          sort={catalog.sort}
          onSortChange={catalog.setSort}
          loading={catalog.loading}
        />

        {/* Error state */}
        {catalog.error && (
          <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="catalog-error">
            <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
            <p className="text-sm text-gray-600 mb-3">{catalog.error}</p>
            <button onClick={catalog.refresh} className="text-sm text-gray-700 underline hover:text-gray-900">
              Réessayer
            </button>
          </div>
        )}

        {/* Loading skeleton */}
        {!catalog.error && catalog.loading && catalog.products.length === 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 py-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-72 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!catalog.error && !catalog.loading && catalog.products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="no-results">
            <p className="text-gray-500 text-sm mb-3">{emptyMessage}</p>
            {hasUserFilters && (
              <button
                onClick={catalog.clearAllFilters}
                className="text-sm text-gray-700 underline hover:text-gray-900"
                data-testid="reset-filters-empty"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {/* Product grid */}
        {!catalog.error && catalog.products.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 py-2">
              {catalog.products.map(product => (
                <div key={product.id} className="relative">
                  <ProductCard
                    product={product}
                    showTag={true}
                    linkPrefix="/dashboard/produit"
                  />
                  {renderProductExtra && renderProductExtra(product)}
                </div>
              ))}
            </div>
            <Pagination
              page={catalog.page}
              totalPages={catalog.totalPages}
              onPageChange={catalog.setPage}
            />
          </>
        )}
      </div>

      {/* Mobile drawer */}
      <SearchFiltersMobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        filters={catalog.filters}
        setFilter={catalog.setFilter}
        clearFilter={catalog.clearFilter}
        clearAllFilters={catalog.clearAllFilters}
        total={catalog.total}
        loading={catalog.loading}
      />
    </div>
  );
}
